// file_upload_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "fileList", "dragDropArea"]
  static values = {
    maxFiles: Number,
    uploadUrl: String
  }

  connect() {
    this.fileCount = 0;
    this.uploadQueue = [];
    this.csrfToken = document.querySelector('meta[name="csrf-token"]').content;    
    this.lockOrUnlockFormSubmission();
    this.setupDragAndDrop();
  }

  setupDragAndDrop() {
    this.dragDropAreaTarget.addEventListener('dragover', (e) => this.handleDragOver(e));
    this.dragDropAreaTarget.addEventListener('drop', (e) => this.handleDrop(e));
  }

  handleDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    this.dragDropAreaTarget.classList.add('drag-over');
  }

  handleDrop(event) {
    event.preventDefault();
    this.dragDropAreaTarget.classList.remove('drag-over');
    const files = event.dataTransfer.files;
    this.handleFiles(files);
  }

  selectFiles(e) {
    e.preventDefault();
    this.inputTarget.click()
  }

  filesSelected(event) {
    const files = event.target.files;
    this.handleFiles(files);
    this.inputTarget.value = ''; // Clear the input for future selections
  }

  handleFiles(files) {
    const validFormats = ['pdf', 'epub', 'csv', 'docx', 'hwp', 'ipynb', 'mbox', 'md', 'ppt', 'pptm', 'pptx'];
    for (let file of files) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!validFormats.includes(fileExtension)) {
        alert(`Invalid file format: ${fileExtension}`);
        continue;
      }
      if (this.fileCount >= this.maxFilesValue) {
        alert(`You can only upload up to ${this.maxFilesValue} files.`);
        break;
      }
      this.addFile(file);
      this.uploadQueue.push(file);
    }
    this.processUploadQueue();
  }

  addFile(file) {
    const fileElement = this.createFileElement(file);
    this.fileListTarget.appendChild(fileElement);
    this.fileCount++;
    this.lockOrUnlockFormSubmission();
  }

  async removeFile(event) {
    const fileElement = event.target.closest('.file-form-control')
    const fileDraftId = fileElement.dataset.fileDraftId

    if (fileDraftId) {
      try {
        const response = await fetch(`${this.uploadUrlValue}${fileDraftId}/delete/`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': this.csrfToken,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to delete file draft')
        }

        this.removeHiddenInput(fileDraftId)
        fileElement.remove()
        this.fileCount--
      } catch (error) {
        console.error('Error deleting file draft:', error)
        alert('Failed to remove the file. Please try again.')
      }
    } else {
      // If there's no fileDraftId, it means the file was not successfully uploaded
      // So we can just remove it from the UI
      fileElement.remove()
      this.fileCount--
    }
    this.lockOrUnlockFormSubmission()
  }

  lockOrUnlockFormSubmission() {
    const generateCourseButton = document.getElementById('generate-course-button')
    if (this.uploadQueue.length > 0) {
      generateCourseButton.disabled = true
    } else {
      generateCourseButton.disabled = false
    }
  }

  createFileElement(file) {
    const div = document.createElement('div')
    div.className = 'file-form-control file-form-control-loading'
    div.dataset.fileName = file.name  // Add this line
    div.innerHTML = `
    <svg class="bi" width="20" height="20" fill="currentColor"><use xlink:href="#file"></use></svg>
    <div class="filename">
      ${file.name}
      <select class="form-select form-select-sm" disabled>
        <option disabled>File weight</option>
        <option value="low">Low</option>
        <option value="medium" selected>Medium</option>
        <option value="high">High</option>
      </select>
    </div>
    <span class="spinner-border spinner-border-sm text-brand" role="status"></span>
  `
    return div
  }

  async processUploadQueue() {
    this.lockOrUnlockFormSubmission();
    while (this.uploadQueue.length > 0) {
      const file = this.uploadQueue.shift()
      await this.uploadFile(file)
    }
    this.lockOrUnlockFormSubmission();
  }

  async uploadFile(file) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('weight', 'medium')  // Default weight

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error('Upload failed')
      }

      const data = await response.json()
      this.addHiddenInput(data.id)
      this.updateFileElement(file.name, data.id)
    } catch (error) {
      console.error('Error uploading file:', error)
      this.updateFileElement(file.name, null, true)
    }
  }

  addHiddenInput(fileDraftId) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'file_drafts'
    input.value = fileDraftId
    this.fileListTarget.appendChild(input)
  }

  removeHiddenInput(fileDraftId) {
    const input = this.fileListTarget.querySelector(`input[name="file_drafts"][value="${fileDraftId}"]`)
    if (input) {
      input.remove()
    }
  }

  updateFileElement(fileName, fileDraftId, error = false) {
    const fileElement = Array.from(this.fileListTarget.children).find(el => el.dataset.fileName === fileName)
    if (fileElement) {
      fileElement.classList.remove('file-form-control-loading')
      if (error) {
        fileElement.classList.add('file-form-control-error')
      } else {
        fileElement.dataset.fileDraftId = fileDraftId
        const select = fileElement.querySelector('select')
        if (select) {
          select.disabled = false
          select.addEventListener('change', (e) => this.updateFileWeight(fileDraftId, e.target.value))
        }
      }
      const spinner = fileElement.querySelector('.spinner-border')
      if (spinner) {
        spinner.remove()
      }

      // Create close icon div
      const closeIcon = document.createElement('div')
      closeIcon.className = 'close-icon'
      closeIcon.innerHTML = `
      <svg class="bi" width="20" height="20" fill="currentColor">
        <use xlink:href="#close"></use>
      </svg>
    `
      closeIcon.addEventListener('click', (e) => this.removeFile(e))

      // Append close icon to file element
      fileElement.appendChild(closeIcon)
    } else {
      console.warn(`File element for "${fileName}" not found`)
    }
  }

  async updateFileWeight(fileDraftId, weight) {
    try {
      const formData = new FormData()
      formData.append('file_draft_id', fileDraftId)
      formData.append('weight', weight)

      const response = await fetch(`${this.uploadUrlValue}${fileDraftId}/`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error('Failed to update file weight')
      }

      // You might want to update the UI here to show the weight has been saved
      console.log(`File weight updated for ${fileDraftId}`)
    } catch (error) {
      console.error('Error updating file weight:', error)
      alert('Failed to update file weight. Please try again.')
    }
  }
}
